import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useScope } from "@/lib/scope";
import { workspacesApi, type WorkspaceRole } from "@/lib/workspaces-api";

export function WorkspaceSettingsPage() {
  const { workspaceId } = useScope();
  const qc = useQueryClient();

  const workspace = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => workspacesApi.get(workspaceId!),
    enabled: !!workspaceId,
  });

  const members = useQuery({
    queryKey: ["workspace", workspaceId, "members"],
    queryFn: () => workspacesApi.members(workspaceId!),
    enabled: !!workspaceId,
  });

  const [name, setName] = useState("");
  const saveName = useMutation({
    mutationFn: () => workspacesApi.update(workspaceId!, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const inviteError =
    members.data?.some((m) => m.email === inviteEmail) ? "Already a member" : null;
  const addMember = useMutation({
    mutationFn: () =>
      workspacesApi.addMember(workspaceId!, inviteEmail, inviteRole),
    onSuccess: () => {
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId, "members"] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      workspacesApi.removeMember(workspaceId!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId, "members"] });
    },
  });

  if (!workspaceId) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-6 p-6 text-sm text-muted-foreground">
        Select a workspace to manage its settings.
      </div>
    );
  }

  if (workspace.isPending) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (workspace.error || !workspace.data) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-6 p-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          {(workspace.error as Error | undefined)?.message ??
            "Workspace not found"}
        </div>
      </div>
    );
  }

  const ws = workspace.data;
  const canEdit = ws.role === "owner" || ws.role === "admin";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Workspace</h1>
        <p className="text-sm text-muted-foreground">
          Settings, plan and members for{" "}
          <span className="font-medium text-foreground">{ws.name}</span>.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>
            Slug <code className="font-mono">{ws.slug}</code> · created{" "}
            {new Date(ws.createdAt).toLocaleDateString()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ws-name">Name</Label>
            <div className="flex gap-2">
              <Input
                id="ws-name"
                value={name || ws.name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
              />
              <Button
                onClick={() => saveName.mutate()}
                disabled={!canEdit || !name || name === ws.name || saveName.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>
            Applies to every project and every member of this workspace.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="text-sm capitalize">
            {ws.subscriptionPlan}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            People with access to every project in this workspace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canEdit && (
            <div className="space-y-2 rounded-lg border bg-muted/20 p-4">
              <Label htmlFor="invite-email">Add member by email</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="teammate@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <select
                  value={inviteRole}
                  onChange={(e) =>
                    setInviteRole(e.target.value as "admin" | "member")
                  }
                  className="rounded-md border bg-background px-2 text-base"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <Button
                  onClick={() => addMember.mutate()}
                  disabled={
                    !inviteEmail || addMember.isPending || !!inviteError
                  }
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
              {addMember.error && (
                <p className="text-sm text-destructive">
                  {(addMember.error as Error).message}
                </p>
              )}
              {inviteError && (
                <p className="text-sm text-muted-foreground">{inviteError}</p>
              )}
            </div>
          )}

          <Separator />

          {members.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <ul className="space-y-2">
              {members.data?.map((m) => (
                <li
                  key={m.userId}
                  className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {m.name || m.email || m.userId}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.email}
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {roleLabel(m.role)}
                  </Badge>
                  {canEdit && m.userId !== ws.ownerUserId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMember.mutate(m.userId)}
                      disabled={removeMember.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function roleLabel(role: WorkspaceRole): string {
  return role;
}
